// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
// import { CopyText } from '../components/common/copy';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  SOW_DENSITY_UNITS_VALUES,
  WEIGHT_UNITS,
  type WeightUnit,
  getWeightUnitOptions,
  utils,
} from '@seedplanner/shared-utils';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import UnitsInputContainer from '../components/common/containers/UnitsInputContainer';
import TableView from '../components/common/view/TableView';
import { useLoggedInState } from '../context/LoggedInContext';
import { useUserContext } from '../context/UserContext';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '../lib/base';
import { Help } from '../lib/help';
import { CenteredView } from '../lib/layout';
import { Link } from '../lib/link';
import * as routes from '../routes';
import { validateNumInput } from '../utils';

export default React.memo(function TrayCalculator() {
  const { preferredWeightUnit } = useUserContext();
  const { isLoggedIn } = useLoggedInState();
  const [sowDensity, setSowDensity] = React.useState('');
  const [unit, setSowDensityUnit] = React.useState(preferredWeightUnit);
  const [yieldPerTray, setYieldPerTray] = React.useState('');
  const [yieldUnit, setYieldUnit] = React.useState(preferredWeightUnit);
  const [bufferPercent, setBufferPercent] = React.useState('');
  const [bufferPercentValidation, setBufferPercentValidation] = React.useState('');
  const [desiredAmount, setDesiredAmount] = React.useState('');
  const [desiredAmountUnit, setDesiredAmountUnit] = React.useState<WeightUnit>(preferredWeightUnit);
  const [yieldPerTrayValidation, setYieldPerTrayValidation] = React.useState('');
  const [sowDensityValidation, setSowDensityValidation] = React.useState('');
  const [desiredAmountValidation, setDesiredAmountValidation] = React.useState('');
  const [shouldPlantWholeTrays, setShouldPlantWholeTrays] = React.useState(false);

  const onChangeShouldPlantWholeTrays = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldPlantWholeTrays(e.target.checked);
  }, []);
  const onChangeDesiredAmount = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDesiredAmount(e.target.value);
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setDesiredAmountValidation(msg),
      validateGT: 0,
      isRequired: true,
    });
  }, []);
  const onDesiredAmountBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setDesiredAmountValidation(msg),
      validateGT: 0,
      isRequired: true,
    });
  }, []);
  const onDesiredAmountUnitChange = React.useCallback((event: SelectChangeEvent<string>) => {
    setDesiredAmountUnit(event.target.value as WeightUnit);
  }, []);
  const onChangeBufferPercent = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setBufferPercent(e.target.value);
    validateNumInput({
      validateInt: true,
      event: e,
      setValidationMessage: (msg) => setBufferPercentValidation(msg),
      validateGTE: 0,
    });
  }, []);
  const onBufferPercentBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      validateInt: true,
      event: e,
      setValidationMessage: (msg) => setBufferPercentValidation(msg),
      validateGTE: 0,
    });
  }, []);
  const onChangeYieldPerTray = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setYieldPerTrayValidation(msg),
      validateGT: 0,
      isRequired: true,
    });
    setYieldPerTray(e.target.value);
  }, []);
  const onYieldPerTrayBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setYieldPerTrayValidation(msg),
      validateGT: 0,
      isRequired: true,
    });
  }, []);
  const onYieldUnitChange = React.useCallback((event: SelectChangeEvent<string>) => {
    setYieldUnit(event.target.value as WeightUnit);
  }, []);
  const onUnitChange = React.useCallback((event: SelectChangeEvent<string>) => {
    setSowDensityUnit(event.target.value as WeightUnit);
  }, []);
  const onChangeSowDensity = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSowDensity(e.target.value);
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setSowDensityValidation(msg),
      validateGT: 0,
    });
  }, []);
  const onSowDensityBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setSowDensityValidation(msg),
      validateGT: 0,
    });
  }, []);

  // Return total trays raw, trays not for buffer, trays for buffer,
  // seed needed, surplus trays, amount for needed, amount for buffer, amount for surplus
  interface TraysAndSeedsNeeded {
    readonly totalTraysRaw: number;
    readonly traysNotForBuffer: number;
    readonly traysForBuffer: number;
    readonly surplusTrays: number;
    readonly amountForBuffer: number;
    readonly amountForSurplus: number;
    readonly seedNeeded: number;
    readonly desiredAmount: number;
    readonly totalAmount: number;
  }
  const getTraysAndSeedsNeeded = React.useCallback((): TraysAndSeedsNeeded => {
    const yieldPerTrayNum = Number.parseFloat(yieldPerTray);
    const desiredAmountNum = Number.parseFloat(desiredAmount);

    if (Number.isNaN(yieldPerTrayNum) || Number.isNaN(desiredAmountNum)) {
      return {
        totalTraysRaw: 0,
        traysNotForBuffer: 0,
        traysForBuffer: 0,
        surplusTrays: 0,
        amountForBuffer: 0,
        amountForSurplus: 0,
        seedNeeded: 0,
        desiredAmount: Number.isNaN(desiredAmountNum) ? 0 : desiredAmountNum,
        totalAmount: 0,
      };
    }
    const bufferPercentNum = Number.parseFloat(bufferPercent);
    const bufferPercentSafe = Number.isNaN(bufferPercentNum) ? 0 : bufferPercentNum;
    const sowDensityNum = Number.parseFloat(sowDensity);
    const sowDensitySafe = Number.isNaN(sowDensityNum) ? 0 : sowDensityNum;
    const yieldPerTrayOunces = utils.convert({
      inputUnit: yieldUnit,
      outputUnit: WEIGHT_UNITS.OUNCES,
      quantity: yieldPerTrayNum,
    });
    const desiredAmountOunces = utils.convert({
      inputUnit: desiredAmountUnit,
      outputUnit: WEIGHT_UNITS.OUNCES,
      quantity: desiredAmountNum,
    });

    const traysRaw = utils.round(
      desiredAmountOunces / yieldPerTrayOunces,
      desiredAmountUnit === WEIGHT_UNITS.GRAMS ? 1 : 2,
    );
    const bufferTrays = utils.round(traysRaw * (bufferPercentSafe / 100));
    const totalTraysRaw = utils.addTraysRaw(traysRaw, bufferTrays);
    const surplusTrays = utils.subtractTraysRaw(Math.ceil(totalTraysRaw), totalTraysRaw);
    const totalPlusSurplus = totalTraysRaw + (shouldPlantWholeTrays ? surplusTrays : 0);
    const seedNeeded = utils.round(totalPlusSurplus * sowDensitySafe);

    // Amount for buffer = yield per tray * buffer trays
    const amountForBuffer = utils.round(
      utils.convert({
        inputUnit: WEIGHT_UNITS.OUNCES,
        outputUnit: desiredAmountUnit,
        quantity: yieldPerTrayOunces * bufferTrays,
      }),
    );
    // Amount for surplus = yield per tray * surplus trays
    const amountForSurplus = utils.round(
      utils.convert({
        inputUnit: WEIGHT_UNITS.OUNCES,
        outputUnit: desiredAmountUnit,
        quantity: yieldPerTrayOunces * surplusTrays,
      }),
    );
    const totalAmount = utils.round(
      utils.convert({
        inputUnit: WEIGHT_UNITS.OUNCES,
        outputUnit: desiredAmountUnit,
        quantity: yieldPerTrayOunces * totalPlusSurplus,
      }),
    );

    return {
      totalTraysRaw,
      traysNotForBuffer: traysRaw,
      traysForBuffer: bufferTrays,
      surplusTrays,
      amountForBuffer,
      amountForSurplus,
      desiredAmount: desiredAmountNum,
      totalAmount,
      seedNeeded,
    };
  }, [yieldPerTray, desiredAmount, bufferPercent, sowDensity, yieldUnit, desiredAmountUnit, shouldPlantWholeTrays]);

  const {
    totalTraysRaw,
    traysNotForBuffer,
    traysForBuffer,
    surplusTrays,
    amountForBuffer,
    amountForSurplus,
    seedNeeded,
    desiredAmount: desiredAmountOut,
    totalAmount,
  } = getTraysAndSeedsNeeded();

  const onClear = React.useCallback(() => {
    setSowDensity('');
    setSowDensityValidation('');
    setYieldPerTray('');
    setYieldPerTrayValidation('');
    setBufferPercent('');
    setBufferPercentValidation('');
    setDesiredAmount('');
    setDesiredAmountValidation('');
  }, []);

  const onEnter = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter') {
        onClear();
      }
    },
    [onClear],
  );

  const form = React.useMemo(
    () => (
      <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
        <UnitsInputContainer
          input={
            <TextField
              aria-describedby="expected-yield"
              id="expected-yield"
              label={`Expected Yield (${yieldUnit} per tray)`}
              value={yieldPerTray}
              onChange={onChangeYieldPerTray}
              onBlur={onYieldPerTrayBlur}
              subtext={yieldPerTrayValidation}
              error={yieldPerTrayValidation !== ''}
              type="number"
              inputMode="decimal"
              required
              numberMin={0}
              hasSubtext
            />
          }
          units={
            <FormControl fullWidth>
              <InputLabel id="yield-units-selector">Units</InputLabel>
              <Select
                aria-describedby="yield-units-selector"
                id="yield-units-selector"
                labelId="yield-units-selector"
                label="Units"
                value={yieldUnit}
                onChange={onYieldUnitChange}
                required={true}
              >
                {getWeightUnitOptions(preferredWeightUnit).map((sizeUnit) => {
                  return (
                    <MenuItem key={sizeUnit} value={sizeUnit}>
                      {sizeUnit}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          }
        />
        <UnitsInputContainer
          input={
            <TextField
              aria-describedby="amount-needed"
              id="amount-needed"
              label={`Amount Needed (${desiredAmountUnit})`}
              value={desiredAmount}
              onChange={onChangeDesiredAmount}
              onBlur={onDesiredAmountBlur}
              subtext={desiredAmountValidation}
              error={desiredAmountValidation !== ''}
              type="number"
              inputMode="decimal"
              required
              numberMin={0}
              hasSubtext
            />
          }
          units={
            <FormControl fullWidth>
              <InputLabel id="desired-amount-selector">Units</InputLabel>
              <Select
                aria-describedby="desired-amount-selector"
                id="desired-amount-selector"
                labelId="desired-amount-selector"
                label="Units"
                value={desiredAmountUnit}
                onChange={onDesiredAmountUnitChange}
                required={true}
              >
                {getWeightUnitOptions(preferredWeightUnit).map((sizeUnit) => {
                  return (
                    <MenuItem key={sizeUnit} value={sizeUnit}>
                      {sizeUnit}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          }
        />
        <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" gap={2}>
          <TextField
            aria-describedby="buffer-percent"
            id="buffer-percent"
            label="Buffer Percentage (optional)"
            value={bufferPercent}
            onChange={onChangeBufferPercent}
            onBlur={onBufferPercentBlur}
            subtext={bufferPercentValidation}
            error={bufferPercentValidation !== ''}
            type="number"
            inputMode="numeric"
            numberMin={0}
            hasSubtext
          />
          <Help tooltip="This will add a buffer to the amount needed. This is useful if you have a crop that you want a buffer for, just in case something goes wrong in the growing process. So if buffer percentage is 30%, and an order will need 10.0 trays for this crop for the given inputs, then it will add an extra 3.0 trays ontop of that" />
        </Box>
        <UnitsInputContainer
          input={
            <TextField
              aria-describedby="sow-density"
              id="sow-density"
              label={`Sow Density (${unit} per tray)`}
              value={sowDensity}
              onChange={onChangeSowDensity}
              onBlur={onSowDensityBlur}
              subtext={sowDensityValidation}
              error={sowDensityValidation !== ''}
              type="number"
              inputMode="decimal"
              numberMin={0}
              hasSubtext
            />
          }
          units={
            <FormControl fullWidth>
              <InputLabel id="units-selector">Units</InputLabel>
              <Select
                aria-describedby="units-selector"
                id="units-selector"
                labelId="units-selector"
                label="Units"
                value={unit}
                onChange={onUnitChange}
                required={true}
              >
                {SOW_DENSITY_UNITS_VALUES.map((sizeUnit) => {
                  return (
                    <MenuItem key={sizeUnit} value={sizeUnit}>
                      {sizeUnit}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          }
        />
        <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" justifyContent="flex-start">
          <FormControlLabel
            label="Plant Whole Trays"
            control={<Checkbox checked={shouldPlantWholeTrays} onChange={onChangeShouldPlantWholeTrays} />}
          />
          <Help tooltip="Checking this will round the total trays need to the nearest whole number and provide surplus calculations." />
        </Box>
      </Box>
    ),
    [
      unit,
      desiredAmountUnit,
      onUnitChange,
      yieldPerTray,
      yieldUnit,
      desiredAmount,
      bufferPercent,
      sowDensity,
      shouldPlantWholeTrays,
      onChangeYieldPerTray,
      onYieldPerTrayBlur,
      yieldPerTrayValidation,
      onYieldUnitChange,
      preferredWeightUnit,
      onChangeShouldPlantWholeTrays,
      desiredAmountValidation,
      onChangeDesiredAmount,
      onDesiredAmountBlur,
      onDesiredAmountUnitChange,
      onChangeBufferPercent,
      onBufferPercentBlur,
      sowDensityValidation,
      onChangeSowDensity,
      onSowDensityBlur,
      bufferPercentValidation,
    ],
  );

  const columns = React.useMemo(
    (): Array<[string, React.ReactElement | string] | [string, React.ReactElement | string, number]> => [
      ['Trays Needed', traysNotForBuffer.toString()],
      ['Trays For Buffer', traysForBuffer.toString()],
      ['Surplus Tray', shouldPlantWholeTrays ? surplusTrays.toString() : '-'],
      ['Total Trays', (shouldPlantWholeTrays ? Math.ceil(totalTraysRaw) : totalTraysRaw).toString()],
      ['Amount Needed', `${desiredAmountOut} ${desiredAmountUnit}`],
      ['Buffer Amount', `${amountForBuffer} ${desiredAmountUnit}`],
      ['Surplus Amount', shouldPlantWholeTrays ? `${amountForSurplus} ${desiredAmountUnit}` : '-'],
      ['Total Amount', `${totalAmount} ${desiredAmountUnit}`],
      ['Seed Needed', `${seedNeeded} ${unit}`],
    ],
    [
      traysNotForBuffer,
      traysForBuffer,
      shouldPlantWholeTrays,
      surplusTrays,
      totalTraysRaw,
      desiredAmountOut,
      desiredAmountUnit,
      unit,
      amountForBuffer,
      amountForSurplus,
      totalAmount,
      seedNeeded,
    ],
  );

  const results = <TableView columns={columns} />;

  return (
    <CenteredView>
      <Helmet>
        <title>Microgreen Tray Calculator</title>
        <meta
          name="description"
          content={
            'Free tray calculator tool to easily calculate how many trays you need to get your desired harvest amount.'
          }
        />
      </Helmet>
      <Container disableGutters component="div" maxWidth="md">
        <Card
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 2,
          }}
        >
          <Typography sx={{ p: 1, textAlign: 'center' }} variant="h4">
            Microgreen Tray Calculator
          </Typography>
          <Box sx={{ mt: 3, width: '100%' }}>
            {form}
            <Box>{results}</Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              onClick={onClear}
              onKeyDown={onEnter}
            >
              Clear
            </Button>
            {!isLoggedIn && (
              <Grid container justifyContent="center">
                <Grid>
                  <Typography sx={{ textAlign: 'center' }} variant="body2">
                    Want more advanced planning and farm management tools like this?{' '}
                    <Link inlineText variant="body2" path={routes.SIGNUP} title="Sign up" /> for Microgreen Manager and
                    see how our software can help you manage your farm!
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>
        </Card>
      </Container>
    </CenteredView>
  );
});
